import { type TravelBookingAdminRequote } from '@utazzo/shared';
import { UzAlert, UzButton, UzDialog, UzSpinner } from '@utazzo/ui';
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { money, signedMoney } from '../booking-format';
import { readRefusal } from '../travel-bookings.api';
import { useIssueTravelBooking, useRequoteTravelBooking } from '../use-travel-bookings';

/**
 * "Book now" on a held booking, as a two-step decision.
 *
 * Step one asks the supplier what the same itinerary costs *now* and shows
 * the difference against what the guest paid - because an hour has passed,
 * the fare or rate session is dead, and a price that moved is the operator's
 * call, not the system's. Step two commits with the delta the operator saw;
 * the server re-quotes once more and refuses (`PRICE_CHANGED`) if it moved
 * further, in which case the fresh figure lands back here instead of being
 * absorbed silently.
 *
 * Nothing in this dialog collects data. The traveller filled everything in
 * before paying; the only input is the decision.
 */
export const BookNowDialog = ({
  open,
  onOpenChange,
  bookingId,
  reference,
  currencyCode,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string;
  reference: string;
  currencyCode: string;
  onDone: () => void;
}): React.JSX.Element => {
  const requote = useRequoteTravelBooking();
  const issue = useIssueTravelBooking();
  const [quote, setQuote] = useState<TravelBookingAdminRequote | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const load = (): void => {
    setRefusal(null);
    requote.mutate(bookingId, {
      onSuccess: setQuote,
      onError: (e) => {
        const r = readRefusal(e);
        setRefusal(r?.message ?? 'Could not re-price this booking. Try again in a moment.');
        if (r?.code === 'NOT_ON_HOLD') onDone();
      },
    });
  };

  useEffect(() => {
    if (open) {
      setQuote(null);
      load();
    }
    // Re-price on every open, never from cache: the whole point is "now".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookingId]);

  const confirm = (): void => {
    if (!quote?.available || quote.deltaPaise === null) return;
    issue.mutate(
      { id: bookingId, input: { acceptedDeltaPaise: quote.deltaPaise } },
      {
        onSuccess: () => {
          onOpenChange(false);
          onDone();
        },
        onError: (e) => {
          const r = readRefusal(e);
          if (!r) {
            toast.error('Booking failed to send. Nothing has changed - try again.');
            return;
          }
          if (r.quote) {
            // PRICE_CHANGED / NOT_AVAILABLE carry the fresh answer: show it in
            // place and let the operator decide again.
            setQuote(r.quote);
            setRefusal(r.message);
            return;
          }
          // ALREADY_HANDLED / NOT_ON_HOLD: the row moved under us.
          toast.warning(r.message);
          onOpenChange(false);
          onDone();
        },
      },
    );
  };

  const busy = requote.isPending || issue.isPending;
  const delta =
    quote?.deltaPaise !== null && quote?.deltaPaise !== undefined ? BigInt(quote.deltaPaise) : null;

  return (
    <UzDialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        onOpenChange(next);
      }}
      title={`Book now - ${reference}`}
      description="Re-priced with the supplier just now. Confirming sends the booking; nothing is charged to the guest again."
      size="lg"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Not now
          </UzButton>
          <div className="flex flex-col gap-2 sm:flex-row">
            <UzButton
              variant="outline"
              onClick={load}
              disabled={busy}
              leftIcon={
                <RefreshCw className={`h-4 w-4 ${requote.isPending ? 'animate-spin' : ''}`} />
              }
            >
              Re-price again
            </UzButton>
            <UzButton
              onClick={confirm}
              disabled={busy || !quote?.available}
              loading={issue.isPending}
              leftIcon={<CheckCircle2 className="h-4 w-4" />}
            >
              {delta !== null && delta > 0n
                ? `Absorb ${money(delta, currencyCode)} and book`
                : 'Confirm and book'}
            </UzButton>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {requote.isPending && (
          <div className="flex items-center gap-3 rounded-md border border-zinc-200 bg-zinc-50 px-4 py-6 text-sm text-zinc-600">
            <UzSpinner />
            Asking the supplier for today’s price. This can take up to a minute.
          </div>
        )}

        {refusal && <UzAlert tone={quote?.available ? 'warning' : 'destructive'} title={refusal} />}

        {quote && !requote.isPending && (
          <>
            {!quote.available ? (
              <UzAlert
                tone="destructive"
                icon={<AlertTriangle className="h-4 w-4" />}
                title="Cannot be booked"
                description={
                  quote.reason ??
                  'The supplier no longer offers this itinerary. The only honest action is a refund.'
                }
              />
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Figure label="Guest paid" value={money(quote.paidPaise, quote.currencyCode)} />
                <Figure
                  label="Costs now"
                  value={money(quote.newSellPaise, quote.currencyCode)}
                  hint={
                    quote.supplierNetAfterMinor
                      ? `supplier net ${money(quote.supplierNetAfterMinor, quote.supplierCurrency)}`
                      : undefined
                  }
                />
                <Figure
                  label="Difference"
                  value={signedMoney(quote.deltaPaise, quote.currencyCode)}
                  tone={delta === null || delta === 0n ? 'neutral' : delta > 0n ? 'bad' : 'good'}
                  hint={
                    delta !== null && delta > 0n
                      ? 'absorbed from margin; recorded on the booking'
                      : delta !== null && delta < 0n
                        ? 'supplier got cheaper; the guest keeps their price'
                        : undefined
                  }
                />
              </div>
            )}

            {(quote.notes.length > 0 || quote.refreshedFromSearch || quote.heldPnr) && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <div className="mb-1 font-semibold">Read before confirming</div>
                <ul className="list-disc space-y-1 pl-4">
                  {quote.heldPnr && (
                    <li>
                      PNR <span className="font-mono">{quote.heldPnr}</span> is still held from the
                      first attempt and will be ticketed, not re-booked.
                    </li>
                  )}
                  {quote.refreshedFromSearch && (
                    <li>
                      The original fare/rate session had expired; this price comes from a fresh
                      search matched to the same itinerary.
                    </li>
                  )}
                  {quote.notes.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </UzDialog>
  );
};

function Figure({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'neutral' | 'good' | 'bad';
}): React.JSX.Element {
  const color =
    tone === 'bad' ? 'text-red-700' : tone === 'good' ? 'text-emerald-700' : 'text-zinc-900';
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-3">
      <div className="text-[11px] font-medium tracking-wide text-zinc-500 uppercase">{label}</div>
      <div className={`mt-1 text-lg font-semibold tabular-nums ${color}`}>{value}</div>
      {hint && <div className="mt-0.5 text-[11px] text-zinc-500">{hint}</div>}
    </div>
  );
}
